/**
 * @file THE sun arc. One pure function owns where the sun is at a given hour,
 * and both the disc and the shading now read it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS AT ALL — THE INVERSION
 * ─────────────────────────────────────────────────────────────────────────────
 * Until the visibility keys landed, `systems/DayNight.js` owned a VISUAL sun
 * that was explicitly forbidden from touching shading: the disc moved, the bake
 * stayed frozen at one 42° direction, and the mismatch was invisible because
 * nothing cast a shadow to contradict the disc.
 *
 * Keys delete that licence. The moment `sunVis` is sampled per hour, a shadow
 * on the ground disagrees with a disc in the sky, and the eye catches it
 * immediately. So there is now exactly ONE arc, it lives here, and both
 * consumers derive from it:
 *
 *   • `systems/DayNight.js`  -> `_sunDir`, for the sky disc and the warm halo.
 *   • `bake/sunKeys.js`      -> the list of directions the keys are baked at.
 *
 * If you ever find a second place computing a sun direction, it is a bug.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ELEVATION BAND IS AN OWNER DECISION, NOT PHYSICS
 * ─────────────────────────────────────────────────────────────────────────────
 * The old arc put the sun 77° up at noon (`elevation = 90 - 15*|h-12|`, capped
 * by a horizon floor). That is an equatorial day, and it bakes a midday with no
 * shadows at all — every roof reads flat and the 52° camera gets nothing to
 * work with. The band below squashes elevation into {@link SUN_ARC.floorDeg} ..
 * {@link SUN_ARC.peakDeg} so a shadow exists at every keyed hour, while the
 * AZIMUTH still sweeps its full 180° across the day. Shadows sweeping round the
 * city is the read we want; a sun passing overhead is not.
 *
 * ⚠ ZERO three.js IMPORTS. `bake.worker.js` pulls this in.
 *
 * @module engine/bake/sunArc
 */

const DEG = Math.PI / 180;
const HOURS_PER_DAY = 24;

/**
 * The arc. `peakDeg`/`floorDeg` are the owner decision documented above;
 * `phase` is inherited verbatim from the old `DayNight.SUN_PHASE` so the sun
 * still rises on the side the gameplay camera looks from (S -> SE -> E). A sun
 * behind the level leaves every roof reading flat, which is the whole reason
 * the phase is not zero.
 *
 * @type {Readonly<{peakDeg: number, floorDeg: number, phase: number,
 *   keyStart: number, keyEnd: number, keyStep: number}>}
 */
export const SUN_ARC = Object.freeze({
  /** Elevation at noon (normalised elevation `el` = +1). */
  peakDeg: 50,
  /** Elevation at 06:00 / 18:00 (`el` = 0). Also fixes the night dip: the curve
   *  is linear in `el`, so midnight lands at `2*floorDeg - peakDeg` = -20°. */
  floorDeg: 15,
  /** Azimuth phase, radians. Was `DayNight.SUN_PHASE`. */
  phase: 4.35,

  // ── the keyed span ────────────────────────────────────────────────────────
  /**
   * Keys cover the hours where the DAY channel is still visible and nothing
   * more. `DayNight`'s night curve is `1 - smoothstep(-0.28, 0.02, el)`, so
   * `uNight` hits a hard 1 at `el = -0.28`, i.e. 04:55 and 19:05. Outside that
   * the day channel is fully crossfaded out and a key there would be baked
   * light nobody can see. 05:00..19:00 at 1 h is 15 keys.
   *
   * ⚠ `keyStep` WAS 0.5 (29 keys) AND IS THE BUILD'S BIGGEST SINGLE LEVER.
   * Solving them is `O(KEY_COUNT)` over ~1.1M unique city vertices, and on the
   * three-city island that measured **29.6 s at 0.5 h against 14.9 s at 1 h**
   * — the whole point of halving it. What it costs is INTERPOLATION DISTANCE,
   * not shadow motion: the pair still blends per frame, so shadows sweep
   * exactly as smoothly, they just interpolate across a 15° azimuth step
   * instead of 7.5°. The visible error is the shadow of a tall building
   * lagging its true direction by up to half a step around mid-morning. Judged
   * in the browser at 12:00, at 17:30, and at 08:00 against 08:30 — the worst
   * case, dead between two keys — and read as one sun half an hour later, not
   * as a blend of two. Halving again (0.25 h) is arithmetic: 57 keys, about a
   * minute of solving, for an error nobody can see. Doubling again (2 h, a 30°
   * step) is where two keys' shadows stop overlapping on a tall building and a
   * crossfade would ghost — REASONED, not measured; nobody has built it.
   */
  keyStart: 5,
  keyEnd: 19,
  keyStep: 1,
});

/** @type {number} 15 for the shipped arc. */
export const KEY_COUNT = Math.round((SUN_ARC.keyEnd - SUN_ARC.keyStart) / SUN_ARC.keyStep) + 1;

/**
 * Normalised sun elevation: +1 at noon, 0 at 06:00/18:00, -1 at midnight.
 *
 * ⚠ This is the SAME curve `DayNight._apply` has always used to drive
 * `uNight` / `uWarm` / the sky stops, and it is deliberately unchanged — the
 * arc rework only alters how a DIRECTION is derived from `el`, never the
 * day/night blend itself. Touching it moves the weather and sky tests.
 *
 * @param {number} hour 0..24
 * @returns {number} -1..1
 */
export function normalisedElevation(hour) {
  return Math.cos(((hour - 12) / 12) * Math.PI);
}

/**
 * Elevation ANGLE for a normalised elevation. Linear in `el`, so the arc is a
 * cosine in time: `peakDeg` at noon, `floorDeg` at the horizon crossings,
 * `2*floorDeg - peakDeg` at midnight.
 *
 * @param {number} el -1..1, from {@link normalisedElevation}.
 * @param {typeof SUN_ARC} [arc]
 * @returns {number} Radians. Negative below the horizon.
 */
export function elevationAngle(el, arc = SUN_ARC) {
  return (arc.floorDeg + (arc.peakDeg - arc.floorDeg) * el) * DEG;
}

/**
 * Azimuth at an hour. Unchanged from the old `DayNight` formula, so the disc
 * still travels the direction it always did.
 *
 * @param {number} hour 0..24
 * @param {typeof SUN_ARC} [arc]
 * @returns {number} Radians.
 */
export function azimuthAngle(hour, arc = SUN_ARC) {
  return arc.phase - (hour / HOURS_PER_DAY) * Math.PI * 2;
}

/**
 * THE sun direction at an hour: surface -> sun, unit length by construction
 * (no `normalize()` call and none needed — `cos^2 + sin^2` is doing it).
 *
 * @param {number} hour 0..24
 * @param {typeof SUN_ARC} [arc]
 * @param {{x: number, y: number, z: number}} [out] Written in place when given,
 *   so the per-frame caller allocates nothing.
 * @returns {{x: number, y: number, z: number}}
 */
export function sunDirAt(hour, arc = SUN_ARC, out = { x: 0, y: 0, z: 0 }) {
  const elev = elevationAngle(normalisedElevation(hour), arc);
  const az = azimuthAngle(hour, arc);
  const c = Math.cos(elev);
  out.x = Math.cos(az) * c;
  out.y = Math.sin(elev);
  out.z = Math.sin(az) * c;
  return out;
}

/**
 * The hour each visibility key is baked at, in order. Index `i` of this array
 * IS key `i` everywhere else in the engine.
 *
 * @param {typeof SUN_ARC} [arc]
 * @returns {number[]} Length {@link KEY_COUNT}.
 */
export function keyHours(arc = SUN_ARC) {
  const n = Math.round((arc.keyEnd - arc.keyStart) / arc.keyStep) + 1;
  const out = new Array(n);
  for (let i = 0; i < n; i++) out[i] = arc.keyStart + i * arc.keyStep;
  return out;
}

/**
 * The directions the keys are baked at — what `bakeSunKeys` consumes.
 *
 * @param {typeof SUN_ARC} [arc]
 * @returns {Array<{x: number, y: number, z: number}>}
 */
export function keyDirs(arc = SUN_ARC) {
  return keyHours(arc).map((h) => sunDirAt(h, arc));
}

/**
 * Which two keys to blend at an hour, and by how much.
 *
 * CLAMPS at both ends rather than wrapping: 03:00 is not "between key 28 and
 * key 0", it is night, `uNight` is 1, and the day channel it would feed is
 * invisible. Wrapping here would spin the shadows backwards through the small
 * hours for nobody's benefit.
 *
 * @param {number} hour 0..24
 * @param {typeof SUN_ARC} [arc]
 * @returns {{a: number, b: number, f: number}} Key indices and the 0..1 blend
 *   from `a` to `b`. `a === b` at both clamps.
 */
export function keyMix(hour, arc = SUN_ARC) {
  const n = Math.round((arc.keyEnd - arc.keyStart) / arc.keyStep) + 1;
  const t = (hour - arc.keyStart) / arc.keyStep;
  if (!(t > 0)) return { a: 0, b: 0, f: 0 };
  if (t >= n - 1) return { a: n - 1, b: n - 1, f: 0 };
  const a = Math.floor(t);
  return { a, b: a + 1, f: t - a };
}
